import { DuePill } from "@/components/DuePill";
import { dragSource, DropZone } from "@/lib/pointerDnd";
import { useEffect, useState, useRef } from "react";
import { agencyApi as api, avatarUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { fmtMinutes, TaskAssigneeFilter, applyTaskFilter, DuePicker } from "@/components/TaskShared";
import { SavedViews } from "@/components/SavedViews";
import { ImportFlowModal } from "@/pages/ConsoleProjects";
import { FlowBadge } from "@/components/contentflow/FlowStages";
import { Workflow } from "lucide-react";
import { Loader2, Plus, Minus, Check, Lock, Users, ArrowLeft, ShieldCheck, FolderLock, Trash2, X, Activity, SlidersHorizontal, ChevronLeft, ChevronRight, Repeat, Gauge } from "lucide-react";

function Avatar({ u, size = 22 }) {
  const url = u?.has_avatar ? avatarUrl(u.id) : null;
  return url
    ? <img src={url} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />
    : <div className="rounded-full bg-[#EDE7FF] text-[#5b2fd6] flex items-center justify-center font-medium" style={{ width: size, height: size, fontSize: size * 0.42 }}>{(u?.name || "?").charAt(0)}</div>;
}

function MembersPicker({ team, value, onChange }) {
  return (
    <div className="max-h-40 overflow-y-auto rounded-xl border border-[#EEEFEF] divide-y divide-[#f0f0f3]">
      {team.map((u) => {
        const on = value.includes(u.id);
        return (
          <button key={u.id} type="button" data-testid={`internal-member-opt-${u.id}`} onClick={() => onChange(on ? value.filter((x) => x !== u.id) : [...value, u.id])}
            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-[#F6F6FD] text-left">
            <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-[#723FFF] border-[#723FFF]" : "border-[#d3d4d8]"}`}>{on && <span className="text-white text-[10px] leading-none">✓</span>}</span>
            <Avatar u={u} size={22} />
            <span className="text-sm text-[#06060C]">{u.name}</span>
            <span className="ml-auto text-[10px] uppercase font-mono-data text-[#9FA1A4]">{u.role}</span>
          </button>
        );
      })}
    </div>
  );
}

function InternalQuickCreate({ status, users, canEstimate, onCancel, onCreate }) {
  const [form, setForm] = useState({ title: "", assignee_id: "none", due_date: "", estimate_min: 0 });
  const submit = () => onCreate({ title: form.title, assignee_id: form.assignee_id === "none" ? null : form.assignee_id, due_date: form.due_date, estimate_min: form.estimate_min });
  const bump = (d) => setForm((f) => ({ ...f, estimate_min: Math.max(0, f.estimate_min + d) }));
  return (
    <div data-testid={`internal-quickcreate-${status}`} className="rounded-xl border border-[#d9ccff] bg-white p-2.5 space-y-2 shadow-sm">
      <input autoFocus data-testid={`internal-add-input-${status}`} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onCancel(); }} placeholder="Task name…" className="w-full rounded-lg border border-[#EEEFEF] px-2.5 py-1.5 text-sm outline-none focus:border-[#d9ccff]" />
      <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
        <SelectTrigger data-testid={`internal-add-assignee-${status}`} className="h-8 bg-[#F6F6FD] border-[#EEEFEF] text-xs"><SelectValue placeholder="Assignee" /></SelectTrigger>
        <SelectContent className="bg-white border-[#EEEFEF] max-h-56"><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
      </Select>
      <DuePicker compact testid={`internal-add-due-${status}`} value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
      {canEstimate && (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-[#F6F6FD] border border-[#EEEFEF] px-2 py-1">
          <span className="text-[11px] text-[#9FA1A4] flex items-center gap-1"><Gauge className="h-3 w-3" /> Estimate</span>
          <div className="flex items-center gap-1">
            <button data-testid={`internal-add-estimate-minus-${status}`} onClick={() => bump(-15)} className="h-6 w-6 rounded-md border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><Minus className="h-3 w-3" /></button>
            <span className="font-mono-data text-xs text-[#06060C] w-12 text-center">{fmtMinutes(form.estimate_min)}</span>
            <button data-testid={`internal-add-estimate-plus-${status}`} onClick={() => bump(15)} className="h-6 w-6 rounded-md border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><Plus className="h-3 w-3" /></button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button data-testid={`internal-add-submit-${status}`} onClick={submit} size="sm" className="flex-1 h-8 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-lg text-xs"><Check className="h-3.5 w-3.5 mr-1" /> Add task</Button>
        <button data-testid={`internal-add-cancel-${status}`} onClick={onCancel} className="h-8 w-8 rounded-lg border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-rose-600"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function InternalActivityPanel({ projectId, open, onOpenChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (open) { setItems(null); api.get(`/console/internal/${projectId}/activity`).then(({ data }) => setItems(data.activity || [])).catch(() => setItems([])); }
  }, [open, projectId]);
  const kindLabel = { status: "moved", comment: "commented", created: "created", assigned: "reassigned", archived: "archived", edited: "edited" };
  const timeAgo = (iso) => { if (!iso) return ""; const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000); if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white border-[#EEEFEF] overflow-y-auto" data-testid="internal-activity-panel">
        <SheetHeader><SheetTitle className="font-display flex items-center gap-2"><Activity className="h-4 w-4 text-[#723FFF]" /> Project activity</SheetTitle><SheetDescription className="text-[#9FA1A4]">Task movements and messages across every task in this internal project.</SheetDescription></SheetHeader>
        {!items ? <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
        : items.length === 0 ? <p className="text-sm text-[#9FA1A4] py-10 text-center">No activity yet.</p>
        : (
          <div className="py-4 space-y-3">
            {items.map((a) => (
              <div key={a.id} data-testid={`internal-activity-${a.id}`} className="flex items-start gap-3">
                <div className="pt-0.5"><Avatar u={{ id: a.actor_id, name: a.actor, has_avatar: a.has_avatar }} size={26} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#06060C] leading-snug"><span className="font-medium">{a.actor || "Someone"}</span> <span className="text-[#9FA1A4]">{kindLabel[a.kind] || a.kind}</span> <span className="font-mono-data text-[11px] text-[#5b2fd6]">{a.task_code}</span></p>
                  {a.kind === "comment" && a.comment ? <p className="text-sm text-[#595D62] mt-0.5 rounded-lg bg-[#F6F6FD] border border-[#f0f0f3] px-2.5 py-1.5">{a.comment}</p> : <p className="text-[13px] text-[#595D62] mt-0.5">{a.text}</p>}
                  <p className="text-[11px] text-[#c4c5cc] font-mono-data mt-0.5 truncate">{a.task_title} · {timeAgo(a.at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function InternalDetailsPanel({ project, team, open, onOpenChange, onSaved }) {
  const isDS = project.is_delivery_success;
  const canManage = project.can_manage && !isDS;
  const [name, setName] = useState(project.name || "");
  const [members, setMembers] = useState((project.members || []).map((m) => m.id));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setName(project.name || ""); setMembers((project.members || []).map((m) => m.id)); }, [project]);
  const dirty = canManage && (name.trim() !== (project.name || "") || JSON.stringify([...members].sort()) !== JSON.stringify((project.members || []).map((m) => m.id).sort()));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/console/internal/${project.id}`, { name: name.trim(), members }); toast.success("Project updated"); onSaved && onSaved({ ...project, name: name.trim() }); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white border-[#EEEFEF] overflow-y-auto" data-testid="internal-details-panel">
        <SheetHeader><SheetTitle className="font-display flex items-center gap-2">{isDS ? <ShieldCheck className="h-5 w-5 text-[#723FFF]" /> : <FolderLock className="h-5 w-5 text-[#723FFF]" />} Project details</SheetTitle><SheetDescription className="text-[#9FA1A4]">{isDS ? "Delivery Success is a team-wide internal project." : "Private internal project — only added members can see it."}</SheetDescription></SheetHeader>
        <div className="space-y-4 py-5">
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Project name</label>{canManage ? <Input data-testid="internal-detail-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" /> : <div className="rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-[#595D62]">{project.name}</div>}</div>
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Client</label><div className="rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-[#595D62]">{project.client?.name || "—"}</div></div>
          <div>
            <label className="text-xs text-[#9FA1A4] mb-2 block">Team members</label>
            {isDS ? <p className="text-sm text-[#595D62] flex items-center gap-1.5"><Users className="h-4 w-4 text-[#723FFF]" /> Everyone on the team</p>
              : canManage ? <MembersPicker team={team} value={members} onChange={setMembers} />
              : (project.members || []).length ? <div className="flex flex-wrap gap-2">{(project.members || []).map((m) => <span key={m.id} className="flex items-center gap-1.5 rounded-full bg-[#F6F6FD] border border-[#EEEFEF] pl-1 pr-2.5 py-0.5"><Avatar u={m} size={20} /><span className="text-xs text-[#06060C]">{m.name}</span></span>)}</div>
              : <p className="text-sm text-[#9FA1A4]">No members yet.</p>}
          </div>
        </div>
        {dirty && <div className="sticky bottom-0 bg-white pt-3 pb-1 border-t border-[#f0f0f3]"><Button data-testid="internal-detail-save" onClick={save} disabled={saving} className="w-full bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save details"}</Button></div>}
      </SheetContent>
    </Sheet>
  );
}

function InternalBoard({ project, canEditTasks, onBack, onChanged, openTaskCode = null, onTaskNavigate }) {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [openTask, setOpenTask] = useState(null);
  const [taskFilter, setTaskFilter] = useState("open");
  const [adding, setAdding] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [proj, setProj] = useState(project);
  const [perPage, setPerPage] = useState(4);
  const [start, setStart] = useState(0);
  const [mode, setMode] = useState("all");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [meId, setMeId] = useState(null);
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/console/internal/${project.id}/tasks`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);
  useEffect(() => { api.get("/console/internal/team").then(({ data }) => setTeam(data.users || [])).catch(() => {}); api.get("/auth/me").then(({ data }) => setMeId(data.id)).catch(() => {}); }, []);
  useEffect(() => { if (!openTaskCode) setOpenTask(null); }, [openTaskCode]);
  useEffect(() => {
    if (!openTaskCode) return;
    let cancelled = false;
    const t = (data?.tasks || []).find((x) => (x.code || "").toLowerCase() === openTaskCode.toLowerCase());
    if (t) { setOpenTask(t.id); return; }
    api.get(`/console/tasks/by-code/${encodeURIComponent(openTaskCode)}`).then(({ data: r }) => { if (!cancelled && r?.id) setOpenTask(r.id); }).catch(() => {});
    return () => { cancelled = true; };
  }, [openTaskCode, data]);
  useEffect(() => {
    const measure = () => { const w = wrapRef.current?.offsetWidth || 1000; setPerPage(Math.max(1, Math.floor((w + 12) / 272))); };
    measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure);
  }, []);

  const create = async (status, form) => {
    const title = (form.title || "").trim();
    if (!title) { setAdding(""); return; }
    try {
      const payload = { project_id: project.id, title, assignee_id: form.assignee_id || null, due_date: form.due_date || null };
      if (canEditTasks && form.estimate_min) payload.time_estimate_minutes = form.estimate_min;
      const t = await api.post("/console/tasks", payload);
      if (status && status !== data.statuses[0]) await api.put(`/console/tasks/${t.data.id}`, { status });
      setAdding(""); await load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [importOpen, setImportOpen] = useState(false);
  const drop = async (t, status) => {
    if (!canEditTasks || t.status === status) return;
    const id = t.id;
    try { await api.put(`/console/tasks/${id}`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const statuses = (() => {
    const all = data?.statuses || [];
    const completed = data?.completed || "Completed";
    return taskFilter === "completed" ? all.filter((s) => s === completed) : taskFilter === "open" ? all.filter((s) => s !== completed) : all;
  })();
  const tasks = applyTaskFilter(data?.tasks || [], { mode, meId, assigneeIds });
  const byStatus = (s) => tasks.filter((t) => t.status === s);
  const maxStart = Math.max(0, statuses.length - perPage);
  const curStart = Math.min(start, maxStart);
  const visible = statuses.slice(curStart, curStart + perPage);
  const paged = statuses.length > perPage;

  return (
    <div data-testid="internal-board">
      {importOpen && <ImportFlowModal projectId={proj.id} onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); load(); }} />}
      <button onClick={onBack} data-testid="internal-board-back" className="flex items-center gap-1.5 text-sm text-[#5b2fd6] hover:text-[#723FFF] mb-4"><ArrowLeft className="h-4 w-4" /> All internal projects</button>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="h-9 w-9 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0">{proj.is_delivery_success ? <ShieldCheck className="h-5 w-5" /> : <FolderLock className="h-5 w-5" />}</div>
            <h2 className="font-display text-2xl font-semibold text-[#06060C] truncate">{proj.name}</h2>
          </div>
          <p className="text-sm text-[#9FA1A4] flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {proj.client?.name ? `${proj.client.name} · ` : ""}{proj.is_delivery_success ? "team-wide internal project" : "private — only added members can see it"}</p>
        </div>
        <div className="flex items-center gap-2">
          {canEditTasks && <Button data-testid="internal-import-flow" onClick={() => setImportOpen(true)} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Workflow className="h-4 w-4 mr-1.5" /> Import Flow</Button>}
          <Button data-testid="internal-view-activity" onClick={() => setActivityOpen(true)} variant="outline" className="border-[#EEEFEF] bg-white text-[#595D62] hover:bg-[#F6F6FD] rounded-full"><Activity className="h-4 w-4 mr-1.5" /> View activity</Button>
          <Button data-testid="internal-view-details" onClick={() => setDetailsOpen(true)} variant="outline" className="border-[#EEEFEF] bg-white text-[#595D62] hover:bg-[#F6F6FD] rounded-full"><SlidersHorizontal className="h-4 w-4 mr-1.5" /> View details</Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white p-1 w-fit" data-testid="internal-task-filter">
            <button data-testid="internal-filter-all" onClick={() => setTaskFilter("all")} className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${taskFilter === "all" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>All tasks</button>
            <button data-testid="internal-filter-open" onClick={() => setTaskFilter("open")} className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${taskFilter === "open" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>Open tasks</button>
            <button data-testid="internal-filter-completed" onClick={() => setTaskFilter("completed")} className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${taskFilter === "completed" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>Completed tasks</button>
          </div>
          <SavedViews screen="internal-board" state={{ taskFilter, mode, assigneeIds }} defaultState={{ taskFilter: "open", mode: "all", assigneeIds: [] }} onApply={(s) => { setTaskFilter(s?.taskFilter || "open"); setMode(s?.mode || "all"); setAssigneeIds(Array.isArray(s?.assigneeIds) ? s.assigneeIds : []); }} />
        </div>
        <TaskAssigneeFilter mode={mode} setMode={setMode} users={team} assigneeIds={assigneeIds} setAssigneeIds={setAssigneeIds} testid="internal" />
      </div>

      {data === null ? <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div> : (
        <div ref={wrapRef} data-testid="internal-kanban">
          {paged && (
            <div className="flex items-center justify-end gap-2 mb-3" data-testid="internal-kanban-pager">
              <span className="font-mono-data text-[11px] text-[#9FA1A4]">Columns {curStart + 1}–{Math.min(statuses.length, curStart + perPage)} of {statuses.length}</span>
              <button data-testid="internal-kanban-prev" onClick={() => setStart(Math.max(0, curStart - 1))} disabled={curStart === 0} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD] disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="internal-kanban-next" onClick={() => setStart(Math.min(maxStart, curStart + 1))} disabled={curStart >= maxStart} className="h-8 w-8 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD] disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
          <div className="grid gap-3 items-start" style={{ gridTemplateColumns: `repeat(${visible.length || 1}, minmax(0, 1fr))` }}>
            {visible.map((s) => (
              <DropZone key={s} id={`internal:${s}`} accept={(t) => t.status !== s && (s !== "Active Flow" || !!t.content_flow)} onDrop={(t) => drop(t, s)} data-testid={`internal-col-${s}`} className={({ isOver }) => `rounded-2xl border p-2 min-w-0 min-h-[120px] transition-colors ${isOver ? "bg-[#EDE7FF] border-[#723FFF] ring-2 ring-[#723FFF]/30" : "bg-[#F6F6FD] border-[#f0f0f3]"}`}>
                <div className="flex items-center justify-between px-2 py-1.5"><span className="text-xs font-semibold text-[#06060C] truncate">{s}</span><span className="font-mono-data text-[10px] text-[#9FA1A4]">{byStatus(s).length}</span></div>
                <div className="space-y-2">
                  {byStatus(s).map((t) => (
                    <div key={t.id} data-testid="internal-kanban-card" {...dragSource(t, { enabled: canEditTasks })} onClick={() => (onTaskNavigate ? onTaskNavigate(t.code) : setOpenTask(t.id))} className="rounded-xl bg-white border border-[#EEEFEF] p-2.5 cursor-pointer hover:border-[#d9ccff] transition-colors">
                      <p className="text-sm text-[#06060C] leading-snug">{t.title}</p>
                      {t.content_flow && <div className="mt-1.5"><FlowBadge t={t} /></div>}
                      <div className="flex items-center justify-between mt-2"><span className="font-mono-data text-[10px] text-[#5b2fd6]">{t.code}</span>{t.assignee && <Avatar u={t.assignee} size={20} />}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <DuePill due={t.due_date} done={t.status === "Completed"} />
                        {t.subtask_count > 0 && <span className="font-mono-data text-[9px] text-[#9FA1A4]">{t.subtask_count} subtasks</span>}
                        {(t.recurrence || t.recurrence_role) && <span title="Recurring" className="inline-flex items-center gap-0.5 font-mono-data text-[9px] text-[#5b2fd6]"><Repeat className="h-3 w-3" /></span>}
                      </div>
                    </div>
                  ))}
                  {canEditTasks && taskFilter !== "completed" && s !== "Active Flow" && (adding === s ? (
                    <InternalQuickCreate status={s} users={team} canEstimate={canEditTasks} onCancel={() => setAdding("")} onCreate={(form) => create(s, form)} />
                  ) : (
                    <button data-testid={`internal-add-${s}`} onClick={() => setAdding(s)} className="w-full flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-[#9FA1A4] hover:bg-white hover:text-[#5b2fd6]"><Plus className="h-3.5 w-3.5" /> Add task</button>
                  ))}
                  {byStatus(s).length === 0 && (taskFilter === "completed" || !canEditTasks) && <p className="px-2.5 py-1.5 text-[11px] text-[#c4c5cc]">None</p>}
                  <div className="dnd-hint rounded-xl border-2 border-dashed border-[#723FFF] bg-white/70 h-14 items-center justify-center text-xs font-medium text-[#723FFF]">Drop here</div>
                </div>
              </DropZone>
            ))}
          </div>
        </div>
      )}

      <InternalDetailsPanel project={proj} team={team} open={detailsOpen} onOpenChange={setDetailsOpen} onSaved={(np) => { setProj((cur) => ({ ...cur, ...np })); onChanged && onChanged(); }} />
      <InternalActivityPanel projectId={project.id} open={activityOpen} onOpenChange={setActivityOpen} />

      {openTask && <TaskDetailDialog taskId={openTask} apiClient={api} users={team} canEdit={canEditTasks} canEstimate={canEditTasks} canMove={canEditTasks} onClose={() => (onTaskNavigate ? onTaskNavigate(null) : setOpenTask(null))} onChanged={() => { load(); onChanged && onChanged(); }} />}
    </div>
  );
}

export default function ConsoleInternal({ scopeClientId = null, openProjectCode = null, openTaskCode = null, onNavigate = null }) {
  const [data, setData] = useState(null);
  const [team, setTeam] = useState([]);
  const [openProject, setOpenProject] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ workspace_id: "", name: "", members: [] });
  const [saving, setSaving] = useState(false);
  const [manageProject, setManageProject] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/console/internal${scopeClientId ? `?client=${scopeClientId}` : ""}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setData({ clients: [] }); }
  };
  useEffect(() => { load(); api.get("/console/internal/team").then(({ data }) => setTeam(data.users || [])).catch(() => {}); /* eslint-disable-next-line */ }, [scopeClientId]);

  // Deep link from a shared task's "View project" when the task lives in an internal project.
  useEffect(() => {
    if (!data || openProject) return;
    let target = null;
    try { target = localStorage.getItem("console_open_internal_project"); } catch (e) {}
    if (!target) return;
    for (const g of (data.clients || [])) {
      const p = (g.projects || []).find((x) => x.id === target);
      if (p) { setOpenProject({ ...p, client: g.client }); break; }
    }
    try { localStorage.removeItem("console_open_internal_project"); } catch (e) {}
  }, [data]); // eslint-disable-line

  // Deep link from a shared clean URL: /console/internal/{PROJECT_CODE}/{TASK_CODE}
  useEffect(() => {
    if (!data || !openProjectCode) return;
    const lc = openProjectCode.toLowerCase();
    for (const g of (data.clients || [])) {
      const p = (g.projects || []).find((x) => (x.project_code || "").toLowerCase() === lc);
      if (p) { setOpenProject((cur) => (cur && cur.id === p.id ? cur : { ...p, client: g.client })); return; }
    }
  }, [data, openProjectCode]);

  const clients = data?.clients || [];
  const canCreate = data?.can_create;

  const openCreate = () => {
    setForm({ workspace_id: scopeClientId || (clients[0]?.client.id || ""), name: "", members: [] });
    setCreateOpen(true);
  };
  const submitCreate = async () => {
    if (!form.name.trim() || !form.workspace_id) { toast.error("Pick a client and enter a name."); return; }
    setSaving(true);
    try {
      await api.post("/console/internal", form);
      toast.success("Internal project created"); setCreateOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveMembers = async (members) => {
    try {
      await api.put(`/console/internal/${manageProject.id}`, { members });
      toast.success("Members updated"); setManageProject(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteProject = async (p) => {
    if (!window.confirm(`Delete internal project "${p.name}"? This removes its tasks.`)) return;
    try { await api.delete(`/console/internal/${p.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (openProject) {
    return (
      <main className="px-6 md:px-10 py-8 max-w-[1600px] mx-auto">
        <InternalBoard project={openProject} canEditTasks={!!openProject.can_edit}
          openTaskCode={onNavigate ? openTaskCode : null}
          onTaskNavigate={onNavigate && openProject.project_code ? ((taskCode) => onNavigate(openProject.project_code, taskCode)) : undefined}
          onBack={() => { setOpenProject(null); onNavigate && onNavigate(null); load(); }} onChanged={load} />
      </main>
    );
  }

  return (
    <main className="px-6 md:px-10 py-8 max-w-5xl mx-auto" data-testid="console-internal">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] flex items-center gap-2.5"><FolderLock className="h-7 w-7 text-[#723FFF]" /> Internal</h1>
          <p className="mt-2 text-sm text-[#9FA1A4]">Private, agency-only projects — including Delivery Success. Add teammates to give them access.</p>
        </div>
        {canCreate && <Button data-testid="internal-create-btn" onClick={openCreate} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New internal project</Button>}
      </div>

      {data === null ? <p className="text-sm text-[#9FA1A4]">Loading…</p>
        : clients.length === 0 ? <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-14 text-center text-sm text-[#9FA1A4]">No internal projects you can access yet.</div>
        : (
          <div className="space-y-8">
            {clients.map((g) => (
              <div key={g.client.id}>
                {!scopeClientId && <p className="font-display font-semibold text-[#06060C] mb-3">{g.client.name}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.projects.map((p) => (
                    <div key={p.id} data-testid="internal-project-card" className="group rounded-2xl border border-[#EEEFEF] bg-white p-4 hover:border-[#d9ccff] transition-colors">
                      <button onClick={() => setOpenProject({ ...p, client: g.client })} className="w-full text-left">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-8 w-8 rounded-lg bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center shrink-0">{p.is_delivery_success ? <ShieldCheck className="h-4 w-4" /> : <FolderLock className="h-4 w-4" />}</div>
                          <span className="font-medium text-[#06060C] truncate">{p.name}</span>
                        </div>
                        <p className="text-xs text-[#9FA1A4]">{p.open_tasks} open task{p.open_tasks === 1 ? "" : "s"}{p.is_delivery_success ? " · team-wide" : ""}</p>
                      </button>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex -space-x-1.5">
                          {(p.members || []).slice(0, 4).map((m) => <span key={m.id} className="ring-2 ring-white rounded-full"><Avatar u={m} size={22} /></span>)}
                          {p.is_delivery_success && !p.members?.length && <span className="text-[10px] text-[#9FA1A4] flex items-center gap-1"><Users className="h-3 w-3" /> Everyone</span>}
                        </div>
                        {p.can_manage && !p.is_delivery_success && (
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button data-testid="internal-manage-members" onClick={() => setManageProject(p)} title="Members" className="text-[#9FA1A4] hover:text-[#5b2fd6]"><Users className="h-4 w-4" /></button>
                            <button data-testid="internal-delete-project" onClick={() => deleteProject(p)} title="Delete" className="text-[#9FA1A4] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="internal-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FolderLock className="h-5 w-5 text-[#723FFF]" /> New internal project</DialogTitle>
            <DialogDescription className="text-[#9FA1A4]">Private to the team members you add. You're added automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!scopeClientId && (
              <div>
                <label className="text-xs text-[#595D62] mb-1 block">Client</label>
                <Select value={form.workspace_id} onValueChange={(v) => setForm((f) => ({ ...f, workspace_id: v }))}>
                  <SelectTrigger data-testid="internal-create-client" className="border-[#EEEFEF]"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#EEEFEF]">
                    {clients.map((g) => <SelectItem key={g.client.id} value={g.client.id}>{g.client.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-[#595D62] mb-1 block">Name</label>
              <Input data-testid="internal-create-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Q4 Planning" className="border-[#EEEFEF]" />
            </div>
            <div>
              <label className="text-xs text-[#595D62] mb-1 block">Team members</label>
              <MembersPicker team={team} value={form.members} onChange={(members) => setForm((f) => ({ ...f, members }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#EEEFEF] bg-transparent rounded-full">Cancel</Button>
            <Button data-testid="internal-create-save" onClick={submitCreate} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {manageProject && (
        <Dialog open onOpenChange={(o) => !o && setManageProject(null)}>
          <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md" data-testid="internal-members-dialog">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Users className="h-5 w-5 text-[#723FFF]" /> {manageProject.name} · members</DialogTitle>
              <DialogDescription className="text-[#9FA1A4]">Only these teammates can view and work in this project.</DialogDescription>
            </DialogHeader>
            <ManageMembers project={manageProject} team={team} onSave={saveMembers} onCancel={() => setManageProject(null)} />
          </DialogContent>
        </Dialog>
      )}
    </main>
  );
}

function ManageMembers({ project, team, onSave, onCancel }) {
  const [members, setMembers] = useState((project.members || []).map((m) => m.id));
  return (
    <>
      <MembersPicker team={team} value={members} onChange={setMembers} />
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} className="border-[#EEEFEF] bg-transparent rounded-full">Cancel</Button>
        <Button data-testid="internal-members-save" onClick={() => onSave(members)} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Save</Button>
      </DialogFooter>
    </>
  );
}
